import React, { useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import moment from 'moment';
import { Col, DatePicker, TimePicker } from 'antd';
import PropTypes from 'prop-types';

import { localToZoneTS, zoneToLocalTS } from '../../../clock/ManualEntry/manualEntryHelpers';

const DATE_FORMAT = 'MMM Do YY';
const TIME_FORMAT = 'hh:mm a';
const TWENTY_FOUR_HOUR_TIME_FORMAT = 'HH:mm';

export default function DateTimeFieldPreview({
  id,
  configProps = {},
  responses = {}, // Actively responding
  previewProps = {}, // Completed form
  setPreviewProps,
  setResponses,
  responding = false,
  isDisplay,
  isFormBuilder,
}) {
  const isFieldDisplay = !isFormBuilder && isDisplay;

  const {
    hideDate = false,
    hideTime = false,
    use24HourTime = false,
    minuteIncrement = 1,
    defaultTime,
  } = configProps;

  const {
    [id]: ourResponses = {},
  } = responses;

  const {
    date,
    time,
    timezone = DateTime.local().zoneName,
  } = responding ? ourResponses : previewProps;

  const dateMoment = date ? moment(zoneToLocalTS(date, timezone)) : null;
  const timeMoment = time ? moment(zoneToLocalTS(time, timezone)) : null;

  const [open, setOpen] = useState(false);
  const [isBlurred, setIsBlurred] = useState(false);

  const onDateChanged = (key) => (newDateMoment) => {
    const newDate = newDateMoment
      ? localToZoneTS(newDateMoment.valueOf(), timezone)
      : newDateMoment;
    if (responding) {
      setResponses({
        ...responses,
        [id]: {
          ...(responses[id] ?? {}),
          [key]: newDate,
          timezone,
        },
      });
    } else {
      setPreviewProps({
        ...previewProps,
        [key]: newDate,
        timezone,
      });
    }
  };

  /*
    HARBOUR-6576:
    When the time picker is closed, we need to set the time to the default time if 'ok' was pressed
    since the onChange will not trigger when value is set to the default time.
    Only do this if the time picker was closed by pressing 'ok' button and not triggered by clicking
    outside the picker (onBlur)
  */
  const handleDefaultTimeChange = () => {
    if (!defaultTime || timeMoment) return;

    const newTime = moment().set({
      hour: defaultTime.hour,
      minute: defaultTime.minute,
    });

    const newTimeDt = localToZoneTS(newTime.valueOf(), timezone);
    if (responding) {
      setResponses({
        ...responses,
        [id]: {
          ...(responses[id] ?? {}),
          time: newTimeDt,
          timezone,
        },
      });
    } else {
      setPreviewProps({
        ...previewProps,
        time: newTimeDt,
        timezone,
      });
    }
  };

  const realTime = useMemo(() => {
    if (timeMoment || !defaultTime || !open) {
      return timeMoment;
    }

    return moment().set({
      hour: defaultTime.hour,
      minute: defaultTime.minute,
    });
  }, [timeMoment, defaultTime, open]);

  if (isFieldDisplay) {
    const displayDate = dateMoment ? dateMoment.format(DATE_FORMAT) : '';
    const timeFormat = use24HourTime ? TWENTY_FOUR_HOUR_TIME_FORMAT : TIME_FORMAT;
    const displayTime = realTime ? realTime.format(timeFormat) : '';
    return (
      <div className="form-field-display-value-text" style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
        {!hideDate && (
          <span>
            {displayDate}
          </span>
        )}
        {!hideTime && (
          <span>
            {displayTime}
          </span>
        )}
      </div>
    );
  }

  return (
    <>
      {!hideDate && (
        <Col style={{ flexGrow: 1, flexBasis: 0 }}>
          <DatePicker
            className="form-field-datetime-container"
            style={{
              width: '100%',
            }}
            onChange={onDateChanged('date')}
            value={dateMoment}
            format={DATE_FORMAT}
          />
        </Col>
      )}
      {!hideTime && (
        <Col style={{ flexGrow: 1, flexBasis: 0 }}>
          <TimePicker
            className="form-field-datetime-container"
            style={{
              width: '100%',
            }}
            onChange={onDateChanged('time')}
            value={realTime}
            format={use24HourTime ? TWENTY_FOUR_HOUR_TIME_FORMAT : TIME_FORMAT}
            use12Hours={!use24HourTime}
            minuteStep={minuteIncrement}
            onOpenChange={(openState) => {
              if (!isBlurred && !openState) {
                handleDefaultTimeChange();
              }
              setOpen(openState);
              setIsBlurred(false);
            }}
            onBlur={() => setIsBlurred(true)}
          />
        </Col>
      )}
    </>
  );
}

/* eslint-disable react/forbid-prop-types */
DateTimeFieldPreview.propTypes = {
  id: PropTypes.string.isRequired,
  configProps: PropTypes.shape({}),
  responses: PropTypes.shape({}),
  previewProps: PropTypes.shape({}),
  setPreviewProps: PropTypes.func.isRequired,
  setResponses: PropTypes.func.isRequired,
  responding: PropTypes.bool,
  isDisplay: PropTypes.bool,
  isFormBuilder: PropTypes.bool,
};

DateTimeFieldPreview.defaultProps = {
  configProps: {},
  responses: {},
  previewProps: {},
  responding: false,
  isDisplay: false,
  isFormBuilder: false,
};
